// SignupPage.jsx
import React, { useState, useContext, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff, Mail, Lock, ArrowRight, UserPlus } from 'lucide-react';
import './AuthPages.css';

const API = process.env.REACT_APP_API_URL || "";

export default function SignupPage() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract return_to, shop, site_id, site_token, access_token, code, hmac, and state from URL query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    //console.log('[SignupPage] (Memo) Current URL Search:', location.search);
    //console.log('[SignupPage] (Memo) Extracted return_to from URL:', params.get("return_to"));
    //console.log('[SignupPage] (Memo) Extracted shop from URL:', params.get("shop"));
    //console.log('[SignupPage] (Memo) Extracted site_id from URL:', params.get("site_id"));
    //console.log('[SignupPage] (Memo) Extracted site_token from URL:', params.get("site_token"));
    //console.log('[SignupPage] (Memo) Extracted access_token from URL:', params.get("access_token"));
    //console.log('[SignupPage] (Memo) Extracted code from URL:', params.get("code"));
    //console.log('[SignupPage] (Memo) Extracted hmac from URL:', params.get("hmac"));
    //console.log('[SignupPage] (Memo) Extracted state from URL:', params.get("state"));
    //console.log('[SignupPage] (Memo) Extracted platform from URL:', params.get("platform")); // Added platform
    return params;
  }, [location.search]);

  const returnToPath = queryParams.get("return_to");
  const shopDomain   = queryParams.get("shop");
  const siteId       = queryParams.get("site_id");
  const siteToken    = queryParams.get("site_token");
  const accessTokenFromShopify = queryParams.get("access_token");
  const code             = queryParams.get("code");
  const hmac             = queryParams.get("hmac");
  const state            = queryParams.get("state");
  const platform         = queryParams.get("platform"); // Added platform

  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);

  // Helper function to handle redirection and site claiming after successful authentication
// Replace the handleAuthSuccess function in SignupPage.jsx (around line 47)

const handleAuthSuccess = async (jwt_access_token) => {
  console.log('[SignupPage] handleAuthSuccess called after successful JWT acquisition.');

  // Step 1: Log the user into your frontend AuthContext
  login(jwt_access_token);

  // Step 2: Call /shopify/link or /api/sites/claim if relevant parameters are present
  if (platform === 'Shopify' && shopDomain && siteId && siteToken && accessTokenFromShopify) {
    console.log(`[SignupPage] Calling /shopify/link to claim Shopify site: shop=${shopDomain}, site_id=${siteId}, site_token=${siteToken}`);
    try {
      const linkParams = new URLSearchParams({
        shop: shopDomain,
        site_id: siteId,
        site_token: siteToken,
        access_token: accessTokenFromShopify
      }).toString();

      const linkRes = await fetch(`${API}/shopify/link?${linkParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt_access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!linkRes.ok) {
        const linkErrorData = await linkRes.json().catch(() => ({}));
        console.error('[SignupPage] Failed to link Shopify store via /shopify/link:', linkErrorData);
        setError(linkErrorData.detail || 'Failed to link Shopify store to your account after signup.');
      } else {
        console.log('[SignupPage] Shopify site linked successfully via /shopify/link.');
      }
    } catch (linkErr) {
      console.error('[SignupPage] Network error during /shopify/link API call:', linkErr);
      setError(linkErr.message || 'Network error while linking Shopify store.');
    }
  } else {
    console.log('[SignupPage] Not all Shopify parameters (shop, site_id, site_token, access_token) found for linking. Skipping /shopify/link call.');
  }

  // Step 3: Redirect the user based on return_to, platform, or default home
  // FIX: Check for return_to first, then platform-specific logic
  let finalRedirectPath;
  
  if (returnToPath) {
    // If return_to is explicitly set, use it (this handles BigCommerce flow)
    finalRedirectPath = returnToPath;
    console.log(`[SignupPage] Using return_to path: ${finalRedirectPath}`);
  } else if (platform === 'BigCommerce' && siteId && siteToken) {
    // Fallback for BigCommerce if return_to wasn't set but we have the params
    const storeHash = new URLSearchParams(location.search).get('store_hash');
    finalRedirectPath = `/link-bigcommerce-account?site_id=${siteId}&site_token=${siteToken}&platform=BigCommerce${storeHash ? `&store_hash=${storeHash}` : ''}`;
    console.log(`[SignupPage] BigCommerce signup, redirecting to: ${finalRedirectPath}`);
  } else {
    // Default to dashboard for authenticated users
    finalRedirectPath = '/home';
    console.log(`[SignupPage] No specific redirect, using default: ${finalRedirectPath}`);
  }

  console.log(`[SignupPage] Final redirect: ${finalRedirectPath}`);
  navigate(finalRedirectPath, { replace: true });
};

  const handleSignup = async e => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      console.warn('[SignupPage] Password mismatch.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      console.warn('[SignupPage] Password too short.');
      return;
    }
    setLoading(true);

    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Signup failed');
      }
      console.log('[SignupPage] Signup API call successful. Proceeding to handleAuthSuccess.');
      handleAuthSuccess(data.access_token);

    } catch (err) {
      console.error('[SignupPage] Signup API error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onGoogleSuccess = ({ credential }) => {
    setError('');
    setLoading(true);
    console.log('[SignupPage] Google signup initiated.');
    fetch(`${API}/auth/oauth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: credential }),
    })
      .then(r => {
        if (!r.ok) {
          return r.json().then(errData => {
            throw new Error(errData?.detail || `Google signup failed: Status ${r.status}`);
          }).catch(() => {
            throw new Error(`Google signup failed: Status ${r.status}`);
          });
        }
        return r.json();
      })
      .then(data => {
        console.log('[SignupPage] Google signup API call successful. Proceeding to handleAuthSuccess.');
        handleAuthSuccess(data.access_token);
      })
      .catch(err => {
        console.error('[SignupPage] Google Signup failed', err);
        setError(err.message || 'Google signup failed.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="auth-page">
      <div className="auth-container-modern">
        {/* Header Section */}
        <div className="auth-header">
          <div className="auth-brand">
          <div className="brand-icon">
          <img src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`} alt="Logo" className="brand-icon" />
            </div>
            <Link to="/" className="auth-link"><h1>BaoBrain</h1></Link>
          </div>
          <div className="auth-welcome">
            <h2>Create your account</h2>
            <p>Start analyzing your website performance today</p>
          </div>
        </div>

        {/* Form Section */}
        <div className="auth-form-container">
          <form className="auth-form-modern" onSubmit={handleSignup}>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle" /* Updated class name */
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle" /* Updated class name */
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && <p className="error-message">{error}</p>}

            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}> {/* Updated class name */}
              {loading ? (
                <>Signing up...</>
              ) : (
                <>
                  <UserPlus size={20} />
                  Sign up
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Google Sign-Up */}
          <div className="auth-divider">
            <span>Or continue with</span>
          </div>

          <div className="google-auth-wrapper">
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={() => {
                setError('Google signup failed');
                console.error('[SignupPage] Google signup onError triggered.');
              }}
              useOneTap={false}
              auto_select={false}
            />
          </div>

          {/* Footer Links */}
          <div className="auth-footer">
            <p>
              Already have an account?{' '}
              {/* Pass all relevant query params to login page */}
              <Link
                to={
                  `/login` +
                  `?shop=${encodeURIComponent(shopDomain || '')}` +
                  `&site_id=${encodeURIComponent(siteId || '')}` +
                  `&site_token=${encodeURIComponent(siteToken || '')}` +
                  `&access_token=${encodeURIComponent(accessTokenFromShopify || '')}` +
                  `&code=${encodeURIComponent(code || '')}` +
                  `&hmac=${encodeURIComponent(hmac || '')}` +
                  `&state=${encodeURIComponent(state || '')}` +
                  `&platform=${encodeURIComponent(platform || '')}` + // Added platform
                  `&return_to=${encodeURIComponent(returnToPath || location.pathname + location.search)}`
                }
                className="auth-link"
              >
                Sign in
                <ArrowRight size={14} />
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Background Design */}
      <div className="auth-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  );
}
